import type { ScoreHistoryPoint } from "../types";

interface ScoreSparklineProps {
  points: ScoreHistoryPoint[];
  width?: number;
  height?: number;
}

const MAX_POINTS = 12; // 스탯 타일 스파크라인 표준 — 최근 12회 관측만

/**
 * 워치 카드용 점수 추이 미니 스파크라인 (Cycle 92).
 * 선은 뮤트 톤(방향 색은 기존 delta 배지가 담당 — 기준점이 달라 색 충돌 방지),
 * 마지막(현재) 관측만 카드 액센트(amber) 점으로 강조. 축·그리드 없음.
 * 점 2개 미만이면 렌더하지 않음(히스토리 축적 전 graceful).
 */
export default function ScoreSparkline({ points, width = 56, height = 28 }: ScoreSparklineProps) {
  if (points.length < 2) return null;

  const recent = points.slice(-MAX_POINTS);
  const scores = recent.map((p) => p.score);
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const span = max - min;
  const pad = 3;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  // 전 구간 동일 점수면 중앙 수평선
  const norm = (s: number) => (span === 0 ? 0.5 : (s - min) / span);
  const coords = recent.map((p, i) => ({
    x: pad + (i / (recent.length - 1)) * innerW,
    y: pad + (1 - norm(p.score)) * innerH,
  }));
  const last = coords[coords.length - 1];
  const fmt = (s: number) => Math.round(s * 10) / 10;
  const label = `최근 ${recent.length}회 점수 추이: ${fmt(recent[0].score)}점 → ${fmt(
    recent[recent.length - 1].score,
  )}점`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      <polyline
        points={coords.map((c) => `${c.x},${c.y}`).join(" ")}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-zinc-300 dark:text-zinc-600"
      />
      <circle cx={last.x} cy={last.y} r="2.5" className="fill-amber-500" />
    </svg>
  );
}
